// Shrink-to-fit for .event-card__title: assets/src/scss/blocks/_events.scss
// sets a larger "starting" font-size per breakpoint (24px desktop / 20px
// mobile). .event-card__content/&__title keep their existing FIXED height
// (not grown) -- a long event title must shrink its own text instead, never
// truncate with an ellipsis. Pure CSS can't conditionally react to "does
// this text still fit its box at this font-size" (no selector/property
// observes real rendered content height), so this needs a real measurement
// loop.
//
// v3 (2026-09-11): fits against the box's own REAL declared height (75px
// desktop / 60px mobile, read from CSS, never hardcoded here) instead of a
// fixed line-count cap -- how many lines that allows differs by breakpoint
// on its own (a wider box's own font-size range fits a different line count
// than a narrower one), so this needs no manual "how many lines on which
// viewport" table to maintain. v2 (hardcoded MAX_LINES=2 everywhere) was a
// deliberate simplification that undercounted how much room the box
// actually has -- reverted after live testing showed real event titles
// naturally landing on 3 lines within the SAME box height budget the v1
// "fits the box" check was already using; capping every breakpoint at a
// flat 2 was stricter than the box geometry requires.
//
// Measuring "does it fit" is NOT a plain scrollHeight/clientHeight
// comparison against the box as rendered -- confirmed twice in testing:
// 1. (v1's actual bug) &__title's own explicit fixed `height` puts a FLOOR
//    under scrollHeight -- it can never read below the box's own set
//    height, even when real (shorter) content doesn't overflow it. Content
//    that only needs 1-2 lines still measured scrollHeight === the full
//    75/60px box, masking whether it actually overflowed or not.
// 2. (v2's own attempted line-count workaround, Range.getClientRects(), was
//    correct in isolation but didn't account for the fact that MORE lines
//    fit in a TALLER font-size range than a SHORTER one -- a flat line-count
//    cap doesn't track the box's own real height budget.)
// Fixed by temporarily setting `height: auto` while measuring -- this lifts
// the floor entirely, so scrollHeight reports the TRUE natural content
// height for the current font-size/width, comparable directly against the
// box's own declared height (read via getComputedStyle BEFORE overriding).
//
// v4 (2026-09-11): before shrinking the font at all, first try reclaiming
// the parent `.event-card__content` wrapper's own unused padding around the
// title. `&__content` is deliberately taller than `&__title` (100px vs 75px
// desktop, 70px vs 60px mobile, matching Figma's own "EventName" frame) and
// centers the title inside itself -- for a SHORT title this ~12px top/bottom
// gap is normal, intentional breathing room, but for a title that doesn't
// fit its own 75/60px box, that same gap is otherwise-wasted space one JS
// pass away from being real usable height. User-reported (live screenshot):
// a pathological-length test title shrank its font far more than the
// visible slack around it justified. Since only a title that ACTUALLY
// doesn't fit its default box ever touches this path, a short title's own
// box is byte-for-byte unchanged from v3 -- this never adds padding to
// other cards, unlike a static CSS height increase would.
const MIN_FONT_PX = 10; // hardcoded safety floor -- reached only by pathological content, not a real title
const BINARY_SEARCH_ITERATIONS = 14; // (maxFontPx - MIN_FONT_PX) halved 14x is well under 0.01px precision for any realistic starting size
const MIN_CONTENT_BREATHING_ROOM_PX = 4; // 2px top + 2px bottom kept even when a title grows into its parent's own space, so text never touches the wrapper's edges

const naturalHeightAt = (el, fontPx) => {
    el.style.fontSize = `${fontPx}px`;
    el.style.height = 'auto';
    const height = el.scrollHeight;
    el.style.height = '';
    return height;
};

export function fitEventCardTitle(el) {
    if (!el) {
        return;
    }

    // Reset to the CSS cascade's own values for the CURRENT @container
    // breakpoint before measuring -- a prior inline override from an
    // earlier (narrower or wider) tier, or a previous shrink pass, must not
    // leak into this one.
    el.style.fontSize = '';
    el.style.height = '';
    const boxHeightPx = Number.parseFloat(getComputedStyle(el).height);
    const maxFontPx = Number.parseFloat(getComputedStyle(el).fontSize);

    if (naturalHeightAt(el, maxFontPx) <= boxHeightPx + 1) {
        el.style.fontSize = ''; // fits at full size already -- no inline override needed
        return;
    }

    // Doesn't fit the title's own default box -- before shrinking the font,
    // see if reclaiming the parent wrapper's own unused padding is enough
    // on its own (see the module-level v4 comment above).
    const parent = el.parentElement;
    const parentHeightPx = parent
        ? Number.parseFloat(getComputedStyle(parent).height)
        : boxHeightPx;
    const growableHeightPx = Math.max(boxHeightPx, parentHeightPx - MIN_CONTENT_BREATHING_ROOM_PX);

    if (naturalHeightAt(el, maxFontPx) <= growableHeightPx + 1) {
        el.style.height = `${growableHeightPx}px`;
        el.style.fontSize = ''; // full font size -- only the box grew, text didn't need to shrink at all
        return;
    }

    // Still doesn't fit even at the grown height -- binary search for the
    // LARGEST font-size in [MIN_FONT_PX, maxFontPx] whose natural
    // (unfloored) content height fits THAT grown height. Every candidate is
    // applied and measured for real (no shortcut estimate), since
    // line-wrapping depends on actual glyph metrics, not just a
    // proportional font-size scale.
    let low = MIN_FONT_PX;
    let high = maxFontPx;
    let best = MIN_FONT_PX;

    for (let i = 0; i < BINARY_SEARCH_ITERATIONS; i += 1) {
        const mid = (low + high) / 2;
        if (naturalHeightAt(el, mid) <= growableHeightPx + 1) {
            best = mid;
            low = mid;
        } else {
            high = mid;
        }
    }

    el.style.height = `${growableHeightPx}px`;
    el.style.fontSize = `${best}px`;
}

export default function initEventCardTitleFit() {
    const findTitles = () => document.querySelectorAll('.event-card__title');

    const fitAll = () => {
        for (const el of findTitles()) {
            fitEventCardTitle(el);
        }
    };

    // document.fonts.ready avoids measuring against a fallback font's
    // metrics right before the real webfont swaps in and reflows text.
    if (document.fonts?.ready) {
        document.fonts.ready.then(fitAll);
    } else {
        fitAll();
    }

    // Re-fit whenever a title's PARENT (.event-card__content) changes size
    // -- covers window resize, this specific card's own @container
    // breakpoint crossing (687px), a Swiper reflow, or a sidebar collapse,
    // without a manual debounced window `resize` listener that can't see
    // container-relative changes the same way. Observing the PARENT, not
    // the title itself, is deliberate since v4: fitEventCardTitle can now
    // set the title's own `height` (reclaiming the parent's unused
    // padding), which DOES change the title's own border-box size -- if
    // this observed the title, that self-inflicted resize would immediately
    // re-trigger itself (each pass converges to the same stable answer, so
    // not an infinite loop, but a wasted extra measurement pass every
    // single time). The parent's own height is fixed by its own CSS
    // (independent of what the title inside it does) and only genuinely
    // changes from an external cause, exactly what this needs to react to.
    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            const title = entry.target.querySelector('.event-card__title');
            if (title) {
                fitEventCardTitle(title);
            }
        }
    });
    for (const el of findTitles()) {
        if (el.parentElement) {
            resizeObserver.observe(el.parentElement);
        }
    }

    // New cards can appear after this module's own initial scan (AJAX
    // pagination via events-ajax.js, Swiper duplicating slides in loop
    // mode) -- one body-level MutationObserver catches all of them without
    // this module needing to know every call site that injects markup.
    const mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) {
                    continue;
                }
                const found = node.matches('.event-card__title')
                    ? [node]
                    : Array.from(node.querySelectorAll?.('.event-card__title') ?? []);
                for (const el of found) {
                    fitEventCardTitle(el);
                    if (el.parentElement) {
                        resizeObserver.observe(el.parentElement);
                    }
                }
            }
        }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
}
